export const OFFICE_PREVIEW_MAX_BLOCKS = 600
export const OFFICE_PREVIEW_MAX_SLIDES = 120
export const OFFICE_PREVIEW_MAX_IMAGE_ASSETS = 48

export type DocumentPreviewParagraphRole = 'title' | 'subtitle' | 'heading' | 'body' | 'list'

export interface DocumentPreviewParagraph {
  type: 'paragraph'
  role: DocumentPreviewParagraphRole
  level: number
  text: string
}

export interface DocumentPreviewTable {
  type: 'table'
  rows: string[][]
  truncated: boolean
}

export interface OfficePreviewImageAsset {
  dataUrl?: string
  width?: number
  height?: number
}

export interface DocumentPreviewImage {
  type: 'image'
  assetId: string
  alt?: string
  width?: number
  height?: number
}

export interface DocumentPreviewDocument {
  kind: 'okclaw-document-preview'
  version: 1
  blocks: Array<DocumentPreviewParagraph | DocumentPreviewTable | DocumentPreviewImage>
  assets: Record<string, OfficePreviewImageAsset>
  truncated: boolean
}

export interface PresentationPreviewParagraph {
  text: string
  level: number
  bullet: boolean
}

export interface PresentationPreviewTextElement {
  type: 'text'
  role: 'title' | 'subtitle' | 'body'
  paragraphs: PresentationPreviewParagraph[]
  fontSize?: number
  bold?: boolean
  x?: number
  y?: number
  width?: number
  height?: number
}

export interface PresentationPreviewTableElement {
  type: 'table'
  rows: string[][]
  x?: number
  y?: number
  width?: number
  height?: number
}

export interface PresentationPreviewImageElement {
  type: 'image'
  assetId: string
  alt?: string
  x?: number
  y?: number
  width?: number
  height?: number
}

export interface PresentationPreviewSlide {
  number: number
  title: string
  elements: Array<PresentationPreviewTextElement | PresentationPreviewTableElement | PresentationPreviewImageElement>
}

export interface PresentationPreviewDocument {
  kind: 'okclaw-presentation-preview'
  version: 1
  slides: PresentationPreviewSlide[]
  slideSize: { width: number; height: number }
  assets: Record<string, OfficePreviewImageAsset>
  truncated: boolean
}

function parseAssets(value: unknown): Record<string, OfficePreviewImageAsset> {
  if (!value || typeof value !== 'object') return {}
  const assets: Record<string, OfficePreviewImageAsset> = {}
  for (const [assetId, asset] of Object.entries(value).slice(0, OFFICE_PREVIEW_MAX_IMAGE_ASSETS)) {
    if (!asset || typeof asset !== 'object') continue
    const dataUrl = typeof (asset as { dataUrl?: unknown }).dataUrl === 'string' ? (asset as { dataUrl: string }).dataUrl : undefined
    if (dataUrl && !/^data:image\/(?:png|jpeg);base64,[a-z0-9+/=]+$/i.test(dataUrl)) continue
    assets[assetId] = {
      dataUrl,
      width: typeof (asset as { width?: unknown }).width === 'number' && Number.isFinite((asset as { width: number }).width) ? Math.max(0, (asset as { width: number }).width) : undefined,
      height: typeof (asset as { height?: unknown }).height === 'number' && Number.isFinite((asset as { height: number }).height) ? Math.max(0, (asset as { height: number }).height) : undefined,
    }
  }
  return assets
}

export function parseDocumentPreview(content: string): DocumentPreviewDocument | null {
  try {
    const value = JSON.parse(content) as Partial<DocumentPreviewDocument>
    if (value.kind !== 'okclaw-document-preview' || value.version !== 1 || !Array.isArray(value.blocks)) return null
    const blocks: Array<DocumentPreviewParagraph | DocumentPreviewTable | DocumentPreviewImage> = []
    for (const block of value.blocks.slice(0, OFFICE_PREVIEW_MAX_BLOCKS)) {
      if (!block || typeof block !== 'object') continue
      if (block.type === 'paragraph' && typeof block.text === 'string') {
        const role = ['title', 'subtitle', 'heading', 'body', 'list'].includes(block.role) ? block.role : 'body'
        blocks.push({
          type: 'paragraph' as const,
          role: role as DocumentPreviewParagraphRole,
          level: Number.isFinite(block.level) ? Math.max(0, Math.min(8, Math.floor(block.level))) : 0,
          text: block.text,
        })
        continue
      }
      if (block.type === 'table' && Array.isArray(block.rows)) {
        blocks.push({
          type: 'table' as const,
          rows: block.rows.slice(0, 120).filter(Array.isArray).map(row => row.slice(0, 24).map(cell => String(cell ?? ''))),
          truncated: block.truncated === true,
        })
        continue
      }
      if (block.type === 'image' && typeof block.assetId === 'string') {
        blocks.push({
          type: 'image' as const,
          assetId: block.assetId,
          alt: typeof block.alt === 'string' ? block.alt : undefined,
          width: typeof block.width === 'number' && Number.isFinite(block.width) ? Math.max(0, block.width) : undefined,
          height: typeof block.height === 'number' && Number.isFinite(block.height) ? Math.max(0, block.height) : undefined,
        })
      }
    }
    return { kind: 'okclaw-document-preview', version: 1, blocks, assets: parseAssets(value.assets), truncated: value.truncated === true }
  } catch {
    return null
  }
}

export function parsePresentationPreview(content: string): PresentationPreviewDocument | null {
  try {
    const value = JSON.parse(content) as Partial<PresentationPreviewDocument>
    if (value.kind !== 'okclaw-presentation-preview' || value.version !== 1 || !Array.isArray(value.slides)) return null
    const slides: PresentationPreviewSlide[] = []
    for (const [index, slide] of value.slides.slice(0, OFFICE_PREVIEW_MAX_SLIDES).entries()) {
      if (!slide || typeof slide !== 'object' || !Array.isArray(slide.elements)) continue
      const elements: Array<PresentationPreviewTextElement | PresentationPreviewTableElement | PresentationPreviewImageElement> = []
      for (const element of slide.elements.slice(0, 64)) {
        if (!element || typeof element !== 'object') continue
        if (element.type === 'table' && Array.isArray(element.rows)) {
          elements.push({
            type: 'table' as const,
            rows: element.rows.slice(0, 40).filter(Array.isArray).map(row => row.slice(0, 16).map(cell => String(cell ?? ''))),
            x: typeof element.x === 'number' && Number.isFinite(element.x) ? Math.max(0, element.x) : undefined,
            y: typeof element.y === 'number' && Number.isFinite(element.y) ? Math.max(0, element.y) : undefined,
            width: typeof element.width === 'number' && Number.isFinite(element.width) ? Math.max(0, element.width) : undefined,
            height: typeof element.height === 'number' && Number.isFinite(element.height) ? Math.max(0, element.height) : undefined,
          })
          continue
        }
        if (element.type === 'text' && Array.isArray(element.paragraphs)) {
          elements.push({
            type: 'text' as const,
            role: element.role === 'title' || element.role === 'subtitle' ? element.role : 'body',
            paragraphs: element.paragraphs.slice(0, 32).filter(Boolean).map(paragraph => ({
              text: String(paragraph.text ?? ''),
              level: Number.isFinite(paragraph.level) ? Math.max(0, Math.min(8, Math.floor(paragraph.level))) : 0,
              bullet: paragraph.bullet === true,
            })),
            fontSize: typeof element.fontSize === 'number' && Number.isFinite(element.fontSize)
              ? Math.max(100, Math.min(20_000, element.fontSize))
              : undefined,
            bold: element.bold === true || undefined,
            x: typeof element.x === 'number' && Number.isFinite(element.x) ? Math.max(0, element.x) : undefined,
            y: typeof element.y === 'number' && Number.isFinite(element.y) ? Math.max(0, element.y) : undefined,
            width: typeof element.width === 'number' && Number.isFinite(element.width) ? Math.max(0, element.width) : undefined,
            height: typeof element.height === 'number' && Number.isFinite(element.height) ? Math.max(0, element.height) : undefined,
          })
          continue
        }
        if (element.type === 'image' && typeof element.assetId === 'string') {
          elements.push({
            type: 'image' as const,
            assetId: element.assetId,
            alt: typeof element.alt === 'string' ? element.alt : undefined,
            x: typeof element.x === 'number' && Number.isFinite(element.x) ? Math.max(0, element.x) : undefined,
            y: typeof element.y === 'number' && Number.isFinite(element.y) ? Math.max(0, element.y) : undefined,
            width: typeof element.width === 'number' && Number.isFinite(element.width) ? Math.max(0, element.width) : undefined,
            height: typeof element.height === 'number' && Number.isFinite(element.height) ? Math.max(0, element.height) : undefined,
          })
        }
      }
      slides.push({ number: Number.isFinite(slide.number) ? slide.number : index + 1, title: typeof slide.title === 'string' && slide.title ? slide.title : `幻灯片 ${index + 1}`, elements })
    }
    if (slides.length === 0) return null
    return {
      kind: 'okclaw-presentation-preview',
      version: 1,
      slides,
      slideSize: { width: Number(value.slideSize?.width) || 13_333_333, height: Number(value.slideSize?.height) || 7_500_000 },
      assets: parseAssets(value.assets),
      truncated: value.truncated === true,
    }
  } catch {
    return null
  }
}

export function isDocumentPreviewContent(content: string): boolean {
  return content.includes('"kind":"okclaw-document-preview"')
}

export function isPresentationPreviewContent(content: string): boolean {
  return content.includes('"kind":"okclaw-presentation-preview"')
}

export function hasUsefulPresentationBounds(
  element: PresentationPreviewTextElement | PresentationPreviewTableElement | PresentationPreviewImageElement,
  slideSize: PresentationPreviewDocument['slideSize'],
): boolean {
  if (![element.x, element.y, element.width, element.height].every(value => typeof value === 'number' && Number.isFinite(value))) return false
  if ((element.width ?? 0) <= 0 || (element.height ?? 0) <= 0) return false
  const x = normalizePresentationCoordinate(element.x ?? 0, slideSize.width)
  const y = normalizePresentationCoordinate(element.y ?? 0, slideSize.height)
  return x < slideSize.width * 1.05 && y < slideSize.height * 1.05
}

export function normalizePresentationCoordinate(value: number, slideDimension: number): number {
  // OOXML uses EMUs, but some otherwise usable generators accidentally write
  // x/y offsets in inches while keeping width/height in EMUs. Values this tiny
  // cannot produce a visible EMU offset, so interpreting them as inches is the
  // safe and useful fallback. Zero remains a real zero origin.
  if (value > 0 && value < slideDimension * 0.0001) return value * 914_400
  return value
}

export function shouldUsePresentationSpatialLayout(
  slide: PresentationPreviewSlide,
  slideSize: PresentationPreviewDocument['slideSize'],
): boolean {
  const positionedCount = slide.elements.filter(element => hasUsefulPresentationBounds(element, slideSize)).length
  return positionedCount >= Math.min(2, slide.elements.length)
    && positionedCount / slide.elements.length >= 0.55
}
